// ./src/components/features/Connectors/ConfigEditors/AwsSqsConnectorConfigEditor.tsx
import React, { useState, useEffect, useCallback } from "react";
import {
  Stack,
  TextInput,
  Text,
  Anchor,
  Alert,
  Code,
  Select,
  NumberInput,
} from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import { stringifyObject, parseObject } from "@/utils";
import { AwsSqsConnectorConfig } from "@/types";

export interface AwsSqsConnectorConfigEditorProps {
  /** JSON string of connector configuration */
  value: string;

  /** Callback when configuration changes */
  onChange: (value: string) => void;

  /** Whether the editor is disabled */
  disabled?: boolean;
}

// Default empty configuration
const DEFAULT_CONFIG: AwsSqsConnectorConfig = {
  accessKeyId: "",
  secretAccessKey: "",
  region: "",
  queueUrl: "",
  messageGroupId: "",
  messageDeduplicationId: "",
  delaySeconds: 0,
};

// Common AWS regions
const AWS_REGIONS = [
  { value: "us-east-1", label: "US East (N. Virginia)" },
  { value: "us-east-2", label: "US East (Ohio)" },
  { value: "us-west-1", label: "US West (N. California)" },
  { value: "us-west-2", label: "US West (Oregon)" },
  { value: "ca-central-1", label: "Canada (Central)" },
  { value: "eu-west-1", label: "Europe (Ireland)" },
  { value: "eu-west-2", label: "Europe (London)" },
  { value: "eu-west-3", label: "Europe (Paris)" },
  { value: "eu-central-1", label: "Europe (Frankfurt)" },
  { value: "eu-north-1", label: "Europe (Stockholm)" },
  { value: "ap-northeast-1", label: "Asia Pacific (Tokyo)" },
  { value: "ap-northeast-2", label: "Asia Pacific (Seoul)" },
  { value: "ap-southeast-1", label: "Asia Pacific (Singapore)" },
  { value: "ap-southeast-2", label: "Asia Pacific (Sydney)" },
  { value: "ap-south-1", label: "Asia Pacific (Mumbai)" },
  { value: "sa-east-1", label: "South America (São Paulo)" },
];

/**
 * Parse and validate AWS SQS connector configuration
 */
const parseAwsSqsConfig = (jsonString: string): AwsSqsConnectorConfig => {
  const parsed = parseObject(jsonString, DEFAULT_CONFIG);
  return parsed;
};

/**
 * Check if queue URL indicates a FIFO queue
 */
const isFifoQueue = (queueUrl: string): boolean => {
  return queueUrl.endsWith(".fifo");
};

export const AwsSqsConnectorConfigEditor: React.FC<
  AwsSqsConnectorConfigEditorProps
> = ({ value, onChange, disabled = false }) => {
  // Parse incoming value to config state
  const [config, setConfig] = useState<AwsSqsConnectorConfig>(() =>
    parseAwsSqsConfig(value)
  );

  // Sync config when external value changes
  useEffect(() => {
    const parsed = parseAwsSqsConfig(value);
    setConfig(parsed);
  }, [value]);

  // Update parent when config changes
  const updateConfig = useCallback(
    (newConfig: AwsSqsConnectorConfig) => {
      setConfig(newConfig);
      onChange(stringifyObject(newConfig, DEFAULT_CONFIG));
    },
    [onChange]
  );

  // Handle field changes
  const handleAccessKeyIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, accessKeyId: e.target.value });
  };

  const handleSecretAccessKeyChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    updateConfig({ ...config, secretAccessKey: e.target.value });
  };

  const handleRegionChange = (value: string | null) => {
    updateConfig({ ...config, region: value || "" });
  };

  const handleQueueUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, queueUrl: e.target.value });
  };

  const handleMessageGroupIdChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    updateConfig({ ...config, messageGroupId: e.target.value });
  };

  const handleMessageDeduplicationIdChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    updateConfig({ ...config, messageDeduplicationId: e.target.value });
  };

  const handleDelaySecondsChange = (value: string | number) => {
    const numValue = typeof value === "string" ? parseInt(value, 10) : value;
    updateConfig({ ...config, delaySeconds: isNaN(numValue) ? 0 : numValue });
  };

  const isFifo = isFifoQueue(config.queueUrl || "");

  // Validate queue URL format
  const queueUrlError = (() => {
    if (!config.queueUrl) return undefined;
    try {
      new URL(config.queueUrl);
      return undefined;
    } catch {
      return "Must be a valid URL";
    }
  })();

  return (
    <Stack gap="md">
      {/* Help Alert */}
      <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
        <Text size="sm">
          Create access keys in{" "}
          <Anchor
            href="https://console.aws.amazon.com/iam/"
            target="_blank"
            rel="noopener noreferrer"
          >
            AWS IAM Console
          </Anchor>
          {". Find queue URL in "}
          <Anchor
            href="https://console.aws.amazon.com/sqs/"
            target="_blank"
            rel="noopener noreferrer"
          >
            SQS Console
          </Anchor>
          {" → Queue → Details"}
        </Text>
      </Alert>

      {/* Access Key ID */}
      <TextInput
        label="Access Key ID"
        placeholder="AKIAIOSFODNN7EXAMPLE"
        description="AWS Access Key ID"
        value={config.accessKeyId}
        onChange={handleAccessKeyIdChange}
        disabled={disabled}
        required
        size="md"
      />

      {/* Secret Access Key */}
      <TextInput
        label="Secret Access Key"
        placeholder="wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY"
        description="AWS Secret Access Key"
        value={config.secretAccessKey}
        onChange={handleSecretAccessKeyChange}
        disabled={disabled}
        required
        size="md"
        type="password"
        autoComplete="off"
      />

      {/* Region */}
      <Select
        label="Region"
        placeholder="Select AWS region"
        description="AWS region where the queue is located"
        data={AWS_REGIONS}
        value={config.region}
        onChange={handleRegionChange}
        disabled={disabled}
        required
        size="md"
        searchable
      />

      {/* Queue URL */}
      <TextInput
        label="Queue URL"
        placeholder="https://sqs.us-east-1.amazonaws.com/123456789012/my-queue"
        description={
          isFifo ? "FIFO queue detected" : "Standard or FIFO queue URL"
        }
        value={config.queueUrl}
        onChange={handleQueueUrlChange}
        disabled={disabled}
        required
        size="md"
        error={queueUrlError}
      />

      {/* FIFO-specific fields */}
      {isFifo && (
        <>
          <TextInput
            label="Message Group ID"
            placeholder="{{formId}}"
            description="Required for FIFO queues. Messages in the same group are processed in order."
            value={config.messageGroupId || ""}
            onChange={handleMessageGroupIdChange}
            disabled={disabled}
            size="md"
          />

          <TextInput
            label="Message Deduplication ID"
            placeholder="{{requestId}}"
            description="Prevents duplicate messages within 5-minute window"
            value={config.messageDeduplicationId || ""}
            onChange={handleMessageDeduplicationIdChange}
            disabled={disabled}
            size="md"
          />
        </>
      )}

      {/* Delay Seconds */}
      <NumberInput
        label="Delay Seconds"
        placeholder="0"
        description="Delay before message becomes visible (0-900 seconds)"
        value={config.delaySeconds || 0}
        onChange={handleDelaySecondsChange}
        disabled={disabled}
        size="md"
        min={0}
        max={900}
      />

      {/* Placeholders Help */}
      <Alert icon={<IconInfoCircle size={16} />} color="gray" variant="light">
        <Text size="sm" fw={500} mb={4}>
          Available placeholders{isFifo ? " for FIFO fields" : ""}:
        </Text>
        <Text size="xs" component="div">
          <Code>{"{{requestId}}"}</Code> — Submission ID (e.g.,
          20250530143022-ABCD)
          <br />
          <Code>{"{{formId}}"}</Code> — Form UUID
          <br />
          <Code>{"{{formName}}"}</Code> — Form name (sanitized)
          <br />
          <Code>{"{{timestamp}}"}</Code> — Unix timestamp
          <br />
          <Code>{"{{date}}"}</Code> — Date (YYYY-MM-DD)
          <br />
          <Code>{"{{randUuid}}"}</Code> — Random UUID
        </Text>
      </Alert>
    </Stack>
  );
};
